import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { 
  Shield, 
  Settings, 
  BarChart3, 
  Megaphone, 
  Briefcase, 
  GraduationCap,
  ChevronDown,
  Check
} from 'lucide-react';
import { logAdminActivity } from '@/utils/adminActivity';

export type UserRole = 
  | 'super_admin' 
  | 'admin' 
  | 'it_admin' 
  | 'platform_admin' 
  | 'marketing' 
  | 'product_manager' 
  | 'intern'
  | 'moderator'
  | 'user';

interface RoleSwitcherProps {
  roles: string[];
  currentRole?: string;
  onRoleChange?: (role: string) => void;
}

const roleConfig: Record<string, { label: string; icon: React.ElementType; color: string; description: string }> = {
  super_admin: {
    label: 'Super Admin',
    icon: Shield,
    color: 'bg-red-500/10 text-red-500 border-red-500/20',
    description: 'Full control over all platforms'
  },
  admin: {
    label: 'Admin',
    icon: Shield,
    color: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    description: 'Full administrative access'
  },
  it_admin: {
    label: 'IT Admin',
    icon: Settings,
    color: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    description: 'Technical settings & system management'
  },
  platform_admin: {
    label: 'Platform Admin',
    icon: Briefcase,
    color: 'bg-green-500/10 text-green-500 border-green-500/20',
    description: 'Manages specific platform'
  },
  marketing: {
    label: 'Marketing',
    icon: Megaphone,
    color: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
    description: 'Marketing tools & campaigns'
  },
  product_manager: {
    label: 'Product Manager',
    icon: BarChart3,
    color: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
    description: 'Analytics & product insights'
  },
  intern: {
    label: 'Intern',
    icon: GraduationCap,
    color: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
    description: 'Read-only access'
  },
  moderator: {
    label: 'Moderator',
    icon: Shield,
    color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    description: 'Content moderation'
  },
  user: {
    label: 'User',
    icon: Briefcase,
    color: 'bg-muted text-muted-foreground border-border',
    description: 'Standard user'
  }
};

export const RoleSwitcher = ({ roles, currentRole, onRoleChange }: RoleSwitcherProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeRole, setActiveRole] = useState(currentRole || roles[0] || 'user');
  
  // Sync with external currentRole prop
  useEffect(() => {
    if (currentRole && currentRole !== activeRole) {
      setActiveRole(currentRole);
    }
  }, [currentRole]);
  
  // Filter to only elevated roles that have admin access
  const elevatedRoles = ['super_admin', 'admin', 'it_admin', 'platform_admin', 'marketing', 'product_manager', 'intern'];
  const adminRoles = roles.filter(role => elevatedRoles.includes(role));
  
  if (adminRoles.length === 0) {
    return null;
  }

  const handleRoleSelect = async (role: string) => {
    const previousRole = activeRole;
    setActiveRole(role);
    localStorage.setItem('active_admin_role', role);
    
    // Audit log role switch
    await logAdminActivity({
      activityType: 'role_switched',
      description: `Switched active role from ${previousRole} to ${role}`,
      resourceType: 'user_session',
      resourceId: role,
      beforeState: { previousRole },
      afterState: { newRole: role }
    });
    
    // Invalidate permission-related queries to force refresh
    queryClient.invalidateQueries({ queryKey: ['navigation-permissions'] });
    queryClient.invalidateQueries({ queryKey: ['role-permissions'] });
    
    onRoleChange?.(role);
    
    // Navigate to admin dashboard when switching to admin role
    if (elevatedRoles.includes(role)) {
      navigate('/admin/dashboard');
    }
  };

  const activeConfig = roleConfig[activeRole] || roleConfig.user;
  const ActiveIcon = activeConfig.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2">
          <ActiveIcon className="h-4 w-4" />
          <span className="hidden sm:inline">{activeConfig.label}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Switch Role</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {adminRoles.map((role) => {
          const config = roleConfig[role] || roleConfig.user;
          const Icon = config.icon;
          const isActive = role === activeRole;
          
          return (
            <DropdownMenuItem
              key={role}
              onClick={() => handleRoleSelect(role)}
              className="flex items-start gap-3 p-3 cursor-pointer"
            >
              <div className={`p-2 rounded-md ${config.color}`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{config.label}</span>
                  {isActive && <Check className="h-4 w-4 text-primary" />}
                </div>
                <p className="text-xs text-muted-foreground">{config.description}</p>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Utility to get role badge
export const RoleBadge = ({ role }: { role: string }) => {
  const config = roleConfig[role] || roleConfig.user;
  const Icon = config.icon;
  
  return (
    <Badge variant="outline" className={`gap-1 ${config.color}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

// Export role config for use elsewhere
export { roleConfig };
